import React from 'react';
import Icon from '../../../components/AppIcon';

const ModuleIntegrationTab = ({ dateRange }) => {
  const integrationInsights = [
    {
      title: 'Canvas Strategy → Financial Outcomes',
      icon: 'GitMerge',
      color: 'primary',
      connections: [
        {
          from: 'Value Proposition refinement',
          to: '15% increase in customer acquisition',
          impact: 'positive',
          metric: '+15%'
        },
        {
          from: 'Cost Structure optimization',
          to: '₹12,000 monthly savings',
          impact: 'positive',
          metric: '₹12k'
        },
        {
          from: 'Revenue Streams diversification',
          to: '3 new income channels identified',
          impact: 'positive',
          metric: '+3'
        }
      ]
    },
    {
      title: 'Goal Feasibility → Actual Performance',
      icon: 'Target',
      color: 'accent',
      connections: [
        {
          from: 'Q1 Revenue Goal: ₹5L',
          to: 'Actual Achievement: ₹4.8L',
          impact: 'neutral',
          metric: '96%'
        },
        {
          from: 'Customer Growth Goal: 200',
          to: 'Actual Growth: 185',
          impact: 'neutral',
          metric: '92.5%'
        },
        {
          from: 'Market Expansion Goal: 2 cities',
          to: 'Actual Expansion: 2 cities',
          impact: 'positive',
          metric: '100%'
        }
      ]
    },
    {
      title: 'Predictive Accuracy → Business Decisions',
      icon: 'TrendingUp',
      color: 'secondary',
      connections: [
        {
          from: 'Seasonal demand forecast',
          to: 'Inventory optimization saved ₹25k',
          impact: 'positive',
          metric: '₹25k'
        },
        {
          from: 'Revenue projection accuracy',
          to: 'Budget allocation improved by 18%',
          impact: 'positive',
          metric: '+18%'
        },
        {
          from: 'Risk prediction model',
          to: 'Prevented 2 potential losses',
          impact: 'positive',
          metric: '2'
        }
      ]
    }
  ];

  const crossModuleMetrics = [
    {
      metric: 'Strategy-Finance Alignment',
      score: 88,
      description: 'Canvas strategies effectively translating to financial results',
      icon: 'GitMerge',
      color: 'primary'
    },
    {
      metric: 'Goal-Performance Correlation',
      score: 92,
      description: 'Set goals accurately reflecting actual business capabilities',
      icon: 'Target',
      color: 'accent'
    },
    {
      metric: 'Prediction-Decision Impact',
      score: 85,
      description: 'Forecasts driving effective strategic decisions',
      icon: 'Lightbulb',
      color: 'secondary'
    },
    {
      metric: 'Overall Integration Health',
      score: 90,
      description: 'Modules working cohesively for business intelligence',
      icon: 'Activity',
      color: 'success'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary/10', text: 'text-primary', icon: 'var(--color-primary)' },
      secondary: { bg: 'bg-secondary/10', text: 'text-secondary', icon: 'var(--color-secondary)' },
      accent: { bg: 'bg-accent/10', text: 'text-accent', icon: 'var(--color-accent)' },
      success: { bg: 'bg-success/10', text: 'text-success', icon: 'var(--color-success)' }
    };
    return colors?.[color] || colors?.primary;
  };

  const getImpactStyles = (impact) => {
    if (impact === 'positive') return { bg: 'bg-success/10', border: 'border-success/20', text: 'text-success' };
    if (impact === 'negative') return { bg: 'bg-error/10', border: 'border-error/20', text: 'text-error' };
    return { bg: 'bg-warning/10', border: 'border-warning/20', text: 'text-warning' };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Cross-Module Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {crossModuleMetrics?.map((item, index) => {
          const colorClasses = getColorClasses(item?.color);
          return (
            <div key={index} className="bg-card border border-border rounded-lg p-3 md:p-4 elevation-sm">
              <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg ${colorClasses?.bg} flex items-center justify-center mb-2 md:mb-3 flex-shrink-0`}>
                <Icon name={item?.icon} size={16} className="md:w-[18px] md:h-[18px]" color={colorClasses?.icon} />
              </div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground font-caption mb-2 break-words">
                {item?.metric}
              </p>
              <p className={`text-2xl md:text-3xl font-bold font-heading ${colorClasses?.text} mb-2`}>
                {item?.score}%
              </p>
              <p className="text-xs text-muted-foreground font-caption break-words">
                {item?.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Integration Insights */}
      {integrationInsights?.map((insight, index) => {
        const colorClasses = getColorClasses(insight?.color);
        return (
          <div key={index} className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
            <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
              <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg ${colorClasses?.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon name={insight?.icon} size={18} className="md:w-5 md:h-5" color={colorClasses?.icon} />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
                {insight?.title}
              </h3>
            </div>
            <div className="space-y-3 md:space-y-4">
              {insight?.connections?.map((connection, connIndex) => {
                const impactStyles = getImpactStyles(connection?.impact);
                return (
                  <div
                    key={connIndex}
                    className={`p-3 md:p-4 border rounded-lg ${impactStyles?.bg} ${impactStyles?.border}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-2 md:gap-3">
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="flex items-start gap-2">
                          <Icon name="ArrowRight" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-muted-foreground)" />
                          <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                            {connection?.from}
                          </p>
                        </div>
                        <div className="flex items-start gap-2 ml-4 md:ml-6">
                          <Icon name="ArrowRight" size={14} className="md:w-4 md:h-4 flex-shrink-0" color={impactStyles?.text?.replace('text-', 'var(--color-') + ')'} />
                          <p className="text-xs md:text-sm text-muted-foreground font-body break-words">
                            {connection?.to}
                          </p>
                        </div>
                      </div>
                      <div className={`px-2.5 md:px-3 py-1 rounded-md ${impactStyles?.bg} border ${impactStyles?.border} flex-shrink-0`}>
                        <span className={`text-xs md:text-sm font-semibold font-body ${impactStyles?.text}`}>
                          {connection?.metric}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Key Takeaways */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
        <div className="flex items-start gap-2 md:gap-3 mb-3 md:mb-4">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={18} className="md:w-5 md:h-5" color="var(--color-primary)" />
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground font-heading">
            Integration Key Takeaways
          </h3>
        </div>
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircle" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-success)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Strong Module Synergy
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                All modules are effectively integrated with 90% overall health score, enabling comprehensive business intelligence
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-accent/10 border border-accent/20 rounded-lg">
            <Icon name="TrendingUp" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-accent)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Data-Driven Decision Making
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                Predictive analytics driving 85% of strategic decisions with measurable positive outcomes
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
            <Icon name="Target" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-secondary)" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-medium text-foreground font-body break-words">
                Goal-Strategy Alignment
              </p>
              <p className="text-xs text-muted-foreground font-caption mt-1 break-words">
                92% correlation between set goals and actual performance indicates realistic planning and effective execution
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleIntegrationTab;